import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaGithub, FaLinkedin } from 'react-icons/fa';

const Contact = ({ darkMode }) => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const [formStatus, setFormStatus] = useState({
        submitting: false,
        submitted: false,
        error: null
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormStatus({ submitting: true, submitted: false, error: null });

        try {
            const response = await fetch('https://formspree.io/f/mgvkkygn', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                setFormStatus({
                    submitting: false,
                    submitted: true,
                    error: null
                });
                setFormData({
                    name: '',
                    email: '',
                    subject: '',
                    message: ''
                });
            } else {
                throw new Error(t('contact.form.error'));
            }
        } catch (error) {
            setFormStatus({
                submitting: false,
                submitted: false,
                error: error.message
            });
        }
    };

    const contactInfo = [
        {
            icon: <FaEnvelope />,
            label: 'Email',
            value: 'starlinluna0102@gmail.com',
            link: 'mailto:starlinluna0102@gmail.com'
        },
        {
            icon: <FaPhone />,
            label: t('contact.info.phone'),
            value: '+1 (809) 750-0508',
            link: 'tel:+18097500508'
        },
        {
            icon: <FaMapMarkerAlt />,
            label: t('contact.info.location'),
            value: 'Santiago de los Caballeros, Rep. Dom.',
            link: 'https://goo.gl/maps/Santiago'
        },
        {
            icon: <FaGithub />,
            label: 'GitHub',
            value: 'github.com/FriasLuna0102',
            link: 'https://github.com/FriasLuna0102'
        },
        {
            icon: <FaLinkedin />,
            label: 'LinkedIn',
            value: 'https://www.linkedin.com/in/starlin-jose-frías-luna-03190628b/',
            link: 'https://www.linkedin.com/in/starlin-jose-frías-luna-03190628b/'
        }
    ];

    return (
        <section id="contact" className={`py-8 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl p-8 shadow-lg`}>
            <h2 className={`text-3xl font-bold mb-8 text-center ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                {t('contact.title')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Información de contacto */}
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <h3 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {t('contact.info.title')}
                        </h3>

                        <div className="space-y-4">
                            {contactInfo.map((info, index) => (
                                <a
                                    key={index}
                                    href={info.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center group"
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 transition-colors ${
                                        darkMode
                                            ? 'bg-gray-600 group-hover:bg-cyan-600 text-cyan-400'
                                            : 'bg-gray-200 group-hover:bg-cyan-500 text-cyan-600 group-hover:text-white'
                                    }`}>
                                        {info.icon}
                                    </div>

                                    <div>
                                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {info.label}
                                        </p>
                                        <p className={`${darkMode ? 'text-gray-200' : 'text-gray-700'} transition-colors group-hover:${
                                            darkMode ? 'text-cyan-300' : 'text-cyan-600'
                                        }`}>
                                            {info.value}
                                        </p>
                                    </div>
                                </a>
                            ))}
                        </div>

                        <div className="mt-8">
                            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {t('contact.info.message')}
                            </p>
                        </div>
                    </div>
                </motion.div>

                {/* Formulario de contacto */}
                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <h3 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            {t('contact.form.title')}
                        </h3>

                        {formStatus.submitted ? (
                            <div className={`p-4 rounded-lg mb-4 ${
                                darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-700'
                            }`}>
                                <p>{t('contact.form.success')}</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <div className="space-y-4">
                                    <div>
                                        <label
                                            htmlFor="name"
                                            className={`block mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                                        >
                                            {t('contact.form.name')}
                                        </label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className={`w-full p-2 rounded-md border ${
                                                darkMode
                                                    ? 'bg-gray-600 border-gray-500 text-white focus:border-cyan-400'
                                                    : 'bg-white border-gray-300 focus:border-cyan-500'
                                            } outline-none transition-colors`}
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="email"
                                            className={`block mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                                        >
                                            {t('contact.form.email')}
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className={`w-full p-2 rounded-md border ${
                                                darkMode
                                                    ? 'bg-gray-600 border-gray-500 text-white focus:border-cyan-400'
                                                    : 'bg-white border-gray-300 focus:border-cyan-500'
                                            } outline-none transition-colors`}
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="subject"
                                            className={`block mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                                        >
                                            {t('contact.form.subject')}
                                        </label>
                                        <input
                                            type="text"
                                            id="subject"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            className={`w-full p-2 rounded-md border ${
                                                darkMode
                                                    ? 'bg-gray-600 border-gray-500 text-white focus:border-cyan-400'
                                                    : 'bg-white border-gray-300 focus:border-cyan-500'
                                            } outline-none transition-colors`}
                                        />
                                    </div>

                                    <div>
                                        <label
                                            htmlFor="message"
                                            className={`block mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                                        >
                                            {t('contact.form.message')}
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows="5"
                                            className={`w-full p-2 rounded-md border ${
                                                darkMode
                                                    ? 'bg-gray-600 border-gray-500 text-white focus:border-cyan-400'
                                                    : 'bg-white border-gray-300 focus:border-cyan-500'
                                            } outline-none transition-colors`}
                                        />
                                    </div>

                                    {formStatus.error && (
                                        <div className={`p-3 rounded-md ${darkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-700'}`}>
                                            <p>{formStatus.error}</p>
                                        </div>
                                    )}

                                    <div>
                                        <button
                                            type="submit"
                                            disabled={formStatus.submitting}
                                            className={`w-full py-2 px-4 rounded-md font-medium ${
                                                darkMode
                                                    ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
                                                    : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                                            } transition-colors ${
                                                formStatus.submitting ? 'opacity-70 cursor-not-allowed' : ''
                                            }`}
                                        >
                                            {formStatus.submitting ? t('contact.form.sending') : t('contact.form.send')}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Contact;